import { createContext, useEffect, useState, useCallback, useMemo } from 'react';
import type { PropsWithChildren } from 'react';
import { useDisclosure } from '@mantine/hooks';
import { useForm } from '@mantine/form';
import { useDebounce } from 'hooks/useDebounce';
import { Box } from '@mantine/core';
import type { UseFormReturnType } from '@mantine/form';
import { zod4Resolver } from 'mantine-form-zod-resolver';
import { ZodType } from 'zod';

export interface DataCallbackProps {
  search: string;
  filters?: Record<string, any>;
  sort?: string;
  page?: number;
  pageSize?: string;
}

export interface CallbackProps {
  search: string;
  filters: Record<string, any>;
  sort: Record<string, 1 | -1>;
  page: number;
  pageSize: number;
  [key: string]: any;
}

interface FormValues {
  data: any[];
  search: string;
  filters: Record<string, any>;
  sort: string;
  page: number;
  pageSize: string;
  total: number;
  [key: string]: any;
}

interface TableProviderProps {
  initialValues?: Record<string, any>;
  initialValuesFilters?: Partial<FormValues>;
  dataCallback: (props: CallbackProps) => Promise<{ data: any; error?: boolean }>;
  dataAdapter: (data: any) => any[];
  sortAdapter: (sortValue: string | undefined) => Record<string, 1 | -1>;
  appId?: string;
  loading: boolean;
  validateSchema?: ZodType<any>;
}

interface TableContextValue {
  form: UseFormReturnType<FormValues, (values: FormValues) => FormValues>;
  showAdvanceSearch: boolean;
  setShowAdvanceSearch: React.Dispatch<React.SetStateAction<boolean>>;
  setFilterValues: (values: Record<string, any>) => void;
  setSearchValue: (value: string) => void;
  setPage: (page: number) => void;
  setPageSize: (pageSize: string) => void;
  setSort: (sort: string) => void;
  cleanFilters: () => void;
  loading: boolean;
  modal: {
    opened: boolean;
    open: () => void;
    close: () => void;
  };
  handleExportAllData: () => Promise<any>;
  handleSubmit: (values: DataCallbackProps) => void;
  selectKey: number;
  setSelectKey: (key: number) => void;
  refetch: () => void;
}

export const TableContext = createContext<TableContextValue>({
  form: {} as UseFormReturnType<FormValues, (values: FormValues) => FormValues>,
  showAdvanceSearch: false,
  setShowAdvanceSearch: () => {},
  setFilterValues: () => {},
  setSearchValue: () => {},
  setPage: () => {},
  setPageSize: () => {},
  setSort: () => {},
  cleanFilters: () => {},
  loading: false,
  modal: {
    opened: false,
    open: () => {},
    close: () => {},
  },
  handleExportAllData: async () => {},
  handleSubmit: (_values: DataCallbackProps) => {},
  selectKey: Date.now(),
  setSelectKey: () => {},
  refetch: () => {},
});

export const TableProvider = ({ children, ...props }: PropsWithChildren<TableProviderProps>) => {
  const {
    initialValues: initialValuesProps,
    initialValuesFilters: initialValuesFiltersProps,
    dataCallback,
    dataAdapter,
    sortAdapter,
    appId,
    loading,
    validateSchema,
  } = props;

  const [showAdvanceSearch, setShowAdvanceSearch] = useState(false);
  const [lastSearch, setLastSearch] = useState('');
  const [lastPropsQuery, setLastPropsQuery] = useState({} as DataCallbackProps);
  const [selectKey, setSelectKey] = useState(Date.now());
  const [opened, { open, close }] = useDisclosure(false);

  const initialValuesFilters = initialValuesFiltersProps || {};

  const form = useForm<FormValues>({
    initialValues: {
      sort: 'date-desc',
      search: '',
      pageSize: '10',
      total: 0,
      page: 1,
      data: [],
      ...initialValuesProps,
      filters: initialValuesFilters,
    },
    validate: validateSchema ? zod4Resolver(validateSchema) : undefined,
    validateInputOnBlur: false,
    validateInputOnChange: false,
    clearInputErrorOnChange: true,
  });

  const debouncedSearch = useDebounce(form.values.search, 1500);

  const setFilterValues = useCallback(
    (values: Record<string, any>) => {
      form.setFieldValue('filters', {
        ...form.values.filters,
        ...values,
      });
    },
    [form.values.filters],
  );

  const setSearchValue = useCallback(
    (value: string) => {
      form.setFieldValue('search', value);
    },
    [form.values.search],
  );

  const setPage = (page: number) => {
    form.setFieldValue('page', page);
  };

  const setPageSize = (pageSize: string) => {
    form.setFieldValue('pageSize', pageSize);
  };

  const setSort = (sort: string) => {
    form.setFieldValue('sort', sort);
  };

  const refetch = () => {
    handleDataCallback(lastPropsQuery);
  };

  const handleSubmit = (values: DataCallbackProps) => {
    if (showAdvanceSearch) {
      handleDataCallback({
        search: '',
        filters: values.filters,
        page: values.page,
        pageSize: values.pageSize,
        sort: values.sort,
      });
    } else {
      handleDataCallback({
        search: values.search,
        filters: initialValuesFilters,
        page: 1,
        pageSize: values.pageSize,
        sort: values.sort,
      });
    }
  };

  const handleDataCallback = async (dataProps: DataCallbackProps) => {
    const { page = 1, pageSize = '10', sort, search, filters } = dataProps;
    let total = 0;
    const sortWithAdapter = sortAdapter(sort);
    const formattedDataProps = {
      search,
      filters,
      page,
      pageSize: +pageSize,
      sort: sortWithAdapter,
      appId: appId || '',
    };
    const { data } = await dataCallback(formattedDataProps);
    const newData = dataAdapter(data?.data || []);

    setLastSearch(search);

    if (JSON.stringify(dataProps) !== JSON.stringify(lastPropsQuery)) {
      setLastPropsQuery(dataProps);
    }
    if (data?.total) {
      total = Math.ceil((data?.total || 0) / +pageSize);
    }
    if (
      JSON.stringify(form.values.data) !== JSON.stringify(newData) ||
      form.values.total !== total
    ) {
      form.setFieldValue('data', newData);
      form.setFieldValue('total', total);
    }
  };

  const handleExportAllData = async () => {
    // Use lastPropsQuery (snapshot of the last fetch that triggered the table)
    // so the export ships the same data the user sees. Only pageSize is
    // overridden (500 = nexus backend max).
    const { sort, search, filters } = lastPropsQuery;
    const sortWithAdapter = sortAdapter(sort);
    const formattedDataProps = {
      search,
      filters,
      pageSize: 500,
      page: 1,
      sort: sortWithAdapter,
      appId: appId || '',
    };
    const { data, error } = await dataCallback(formattedDataProps);
    const exportAllData = dataAdapter(data?.data || []);
    return {
      data: exportAllData,
      total: data?.total || 0,
      error,
    };
  };

  const cleanFilters = async () => {
    setSelectKey(Date.now());
    const defaultProps = {
      search: '',
      filters: initialValuesFilters,
      page: 1,
      pageSize: '10',
      sort: form.values.sort,
    };

    form.setValues(defaultProps);
    if (lastPropsQuery.page === defaultProps.page || lastPropsQuery.sort === defaultProps.sort) {
      if (JSON.stringify(defaultProps) !== JSON.stringify(lastPropsQuery)) {
        handleSubmit(defaultProps);
      }
    }
  };

  useEffect(() => {
    cleanFilters();
  }, [showAdvanceSearch, appId]);

  useEffect(() => {
    if (!showAdvanceSearch && form.values.search !== lastSearch) {
      if (form.values.page !== 1) {
        form.setValues({
          filters: initialValuesFilters,
          page: 1,
        });
      } else {
        handleDataCallback({
          search: form.values.search,
          filters: initialValuesFilters,
          page: 1,
          pageSize: form.values.pageSize,
        });
      }
    }
  }, [debouncedSearch]);

  useEffect(() => {
    if (form.values.sort !== lastPropsQuery.sort && form.values.page !== 1) {
      /* If the sort changes and the page is different from 1, 
      restart the pagination, which triggers this effect again. */
      form.setFieldValue('page', 1);
    } else {
      const newDataProps = {
        search: form.values.search,
        filters: form.values.filters,
        pageSize: form.values.pageSize,
        sort: form.values.sort,
        page: form.values.page,
      };
      if (JSON.stringify(newDataProps) !== JSON.stringify(lastPropsQuery)) {
        handleDataCallback(newDataProps);
      }
    }
  }, [form.values.page, form.values.sort]);

  const contextValue = useMemo<TableContextValue>(
    () => ({
      form,
      showAdvanceSearch,
      setShowAdvanceSearch,
      setFilterValues,
      setSearchValue,
      setPage,
      setPageSize,
      setSort,
      cleanFilters,
      loading,
      modal: { opened, open, close },
      handleExportAllData,
      handleSubmit,
      selectKey,
      setSelectKey,
      refetch,
    }),
    [
      form,
      showAdvanceSearch,
      setShowAdvanceSearch,
      setFilterValues,
      setSearchValue,
      setPage,
      setPageSize,
      setSort,
      cleanFilters,
      loading,
      opened,
      open,
      close,
      handleExportAllData,
      handleSubmit,
      selectKey,
      setSelectKey,
      refetch,
    ],
  );

  return (
    <TableContext.Provider value={contextValue} key={appId}>
      <form onSubmit={form.onSubmit(handleSubmit)}>
        <Box>{children}</Box>
      </form>
    </TableContext.Provider>
  );
};
